import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuthStore } from '../auth/useAuthStore'
import { toast } from '../toast/useToastStore'
import type { AccountSummary } from '../api/types'

interface Props {
  onChange: (accountId: string) => void
  variant?: 'standalone' | 'header'
}

function labelFor(a: AccountSummary): string {
  if (a.displayName) return a.displayName
  const map: Record<string, string> = {
    BROKERAGE: 'Brokerage',
    HIGH_YIELD: 'High-Yield Cash',
    BOND_ACCOUNT: 'Bonds',
    RIA_ASSET: 'RIA',
    TREASURY: 'Treasury',
    TRADITIONAL_IRA: 'Traditional IRA',
    ROTH_IRA: 'Roth IRA',
  }
  return map[a.accountType] ?? a.accountType
}

function displayId(a: AccountSummary): string {
  return a.accountNumber ?? a.accountId
}

function AccountTypeBadge({ account }: { account: AccountSummary }) {
  if (account.accountType !== 'BROKERAGE' || !account.brokerageAccountType) return null
  const isMargin = account.brokerageAccountType.toUpperCase() === 'MARGIN'
  return (
    <span
      className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded border ${
        isMargin
          ? 'bg-amber-950/50 text-amber-300 border-amber-900'
          : 'bg-sky-950/50 text-sky-300 border-sky-900'
      }`}
    >
      {account.brokerageAccountType.toLowerCase()}
    </span>
  )
}

function PlatformPill({ name }: { name: string }) {
  return (
    <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded border bg-zinc-900 text-zinc-400 border-zinc-700">
      {name}
    </span>
  )
}

function ArmToggle({ armed, onToggle }: { armed: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle() }}
      className={`relative w-8 h-[18px] rounded-full transition-colors shrink-0 ${
        armed ? 'bg-emerald-500/25' : 'bg-zinc-700'
      }`}
      title={armed ? 'Armed for fleet trading — click to disarm' : 'Click to arm for fleet trading'}
      aria-label={armed ? 'Disarm account' : 'Arm account'}
    >
      <span
        className={`absolute top-[2px] w-[14px] h-[14px] rounded-full transition-all ${
          armed
            ? 'left-4 bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]'
            : 'left-[2px] bg-zinc-400'
        }`}
      />
    </button>
  )
}

export function AccountSwitcher({ onChange, variant = 'standalone' }: Props) {
  const accounts = useAuthStore((s) => s.accounts)
  const accountId = useAuthStore((s) => s.accountId)
  const platforms = useAuthStore((s) => s.platforms)
  const scopeAccountId = useAuthStore((s) => s.scopeAccountId)
  const setScope = useAuthStore((s) => s.setScope)
  const setArmed = useAuthStore((s) => s.setArmed)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const t = e.target as Node
      if (ref.current && ref.current.contains(t)) return
      if (menuRef.current && menuRef.current.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Anchor the portaled menu to the trigger in viewport coordinates so it
  // escapes the header's stacking context / overflow clipping.
  useLayoutEffect(() => {
    if (!open) return
    function place() {
      const r = ref.current?.getBoundingClientRect()
      if (!r) return
      setPos({ top: r.bottom, right: Math.max(8, window.innerWidth - r.right) })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  const platformNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of platforms) map.set(p.platformId, p.displayName)
    return map
  }, [platforms])

  const grouped = useMemo(() => {
    // Preserve the order platforms appear in the store (created_at asc on
    // the server), then sort accounts within each group by their existing
    // store order. Falls back to a synthesized group for any account whose
    // platform is missing from the platforms list.
    const order: string[] = []
    const map = new Map<string, AccountSummary[]>()
    for (const p of platforms) {
      order.push(p.platformId)
      map.set(p.platformId, [])
    }
    for (const a of accounts) {
      if (!map.has(a.platformId)) {
        order.push(a.platformId)
        map.set(a.platformId, [])
      }
      map.get(a.platformId)!.push(a)
    }
    return order
      .map((pid) => ({
        platformId: pid,
        displayName: platformNameById.get(pid) ?? pid,
        accounts: map.get(pid) ?? [],
      }))
      .filter((g) => g.accounts.length > 0)
  }, [accounts, platforms, platformNameById])

  const showHeadings = grouped.length > 1
  const current = accounts.find((a) => a.accountId === accountId)
  const armedCount = useMemo(() => accounts.filter(a => a.armed).length, [accounts])
  const currentPlatformName = current ? platformNameById.get(current.platformId) ?? null : null
  const isAggregate = scopeAccountId === null && accounts.length > 1

  if (accounts.length === 0) return null

  const isHeader = variant === 'header'
  const containerClass = isHeader ? 'relative h-full min-w-0' : 'relative'
  const buttonClass = isHeader
    ? `h-full max-w-full flex items-center gap-1.5 border-x rounded-none px-2 sm:px-4 text-sm transition-colors ${
        isAggregate
          ? 'bg-emerald-950/40 hover:bg-emerald-950/60 border-emerald-900/60'
          : 'bg-transparent hover:bg-zinc-900 border-zinc-800'
      }`
    : `flex items-center gap-2 rounded px-3 py-1.5 text-sm transition-colors ${
        isAggregate
          ? 'bg-emerald-950/40 hover:bg-emerald-950/60 border border-emerald-700/60'
          : 'bg-zinc-900 hover:bg-zinc-800 border border-zinc-700'
      }`
  const dropdownClass = isHeader
    ? 'fixed w-72 max-w-[calc(100vw-1rem)] max-h-[80vh] overflow-y-auto bg-zinc-900 border border-zinc-800 shadow-xl z-[60]'
    : 'fixed mt-1 w-72 max-w-[calc(100vw-1rem)] max-h-[80vh] overflow-y-auto bg-zinc-900 border border-zinc-700 rounded shadow-xl z-[60]'

  return (
    <div ref={ref} className={containerClass}>
      <button onClick={() => setOpen((o) => !o)} className={buttonClass}>
        <div className="text-left min-w-0">
          {isAggregate ? (
            <>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-emerald-300 font-semibold truncate">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                  All accounts
                </span>
                <span className="hidden sm:inline-flex text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded border bg-emerald-950/60 text-emerald-300 border-emerald-800">
                  aggregate
                </span>
              </div>
              <div className="hidden sm:block text-xs text-emerald-400/70 truncate max-w-[14rem]">
                {accounts.length} accounts · {armedCount} armed
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-zinc-100 truncate">{current ? labelFor(current) : 'Select account'}</span>
                {current && <AccountTypeBadge account={current} />}
              </div>
              <div className="hidden sm:flex items-center gap-1.5 text-xs text-zinc-500 font-mono truncate max-w-[14rem]">
                <span className="truncate">{current ? displayId(current) : ''}</span>
                {currentPlatformName && (
                  <span className="text-zinc-600 normal-case font-sans">· {currentPlatformName}</span>
                )}
              </div>
            </>
          )}
        </div>
        <span
          className={`hidden sm:inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono border ${
            isAggregate
              ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
              : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
          }`}
        >
          {armedCount}/{accounts.length}
        </span>
        <span className={`text-xs shrink-0 ${isAggregate ? 'text-emerald-400/70' : 'text-zinc-500'}`}>▾</span>
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          className={dropdownClass}
          style={{ top: pos.top, right: pos.right }}
        >
          {grouped.map((group, gi) => (
            <div key={group.platformId} className={gi > 0 ? 'border-t border-zinc-800' : ''}>
              {showHeadings && (
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-zinc-500">
                  {group.displayName}
                </div>
              )}
              {group.accounts.map((a) => {
                const selected = a.accountId === accountId
                return (
                  <div
                    key={a.accountId}
                    className={`group w-full text-left px-3 py-2 flex items-center justify-between gap-3 hover:bg-zinc-800 transition-colors ${
                      selected ? 'bg-zinc-800/60' : ''
                    } ${a.armed ? 'shadow-[inset_3px_0_0_#10b981]' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false)
                        setScope(a.accountId)
                        if (a.accountId !== accountId) onChange(a.accountId)
                      }}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-100">{labelFor(a)}</span>
                        <AccountTypeBadge account={a} />
                        {!showHeadings && <PlatformPill name={platformNameById.get(a.platformId) ?? a.platformId} />}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono truncate" title={displayId(a)}>
                        {displayId(a)}
                      </div>
                      <div className="text-[10px] text-zinc-600 uppercase tracking-wide mt-0.5">
                        {(a.tradePermissions ?? '').replaceAll('_', ' ').toLowerCase()}
                      </div>
                    </button>
                    <ArmToggle
                      armed={!!a.armed}
                      onToggle={() => {
                        const arming = !a.armed
                        setArmed(a.accountId, arming).catch((err) => {
                          // Store already rolled back the optimistic toggle.
                          toast.error(
                            `${arming ? 'Arming' : 'Disarming'} ${labelFor(a)} failed — ${
                              err instanceof Error ? err.message : 'unknown error'
                            }`,
                          )
                        })
                      }}
                    />
                  </div>
                )
              })}
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              setScope(null)
            }}
            className={`w-full text-left px-3 py-2 border-t border-zinc-800 bg-emerald-950/20 hover:bg-emerald-950/30 flex items-center justify-between transition-colors ${
              scopeAccountId === null ? 'bg-emerald-950/40' : ''
            }`}
          >
            <span className="text-sm text-emerald-300 font-medium">All accounts (aggregate)</span>
            <span className="text-[11px] text-zinc-500 font-mono">{armedCount} armed</span>
          </button>
        </div>,
        document.body,
      )}
    </div>
  )
}
